import { DEMO_BILLING_NOTE, isDemo } from '../account/beta.ts';
import { useAccount } from '../account/store.ts';
import { apiMessage } from '../api/errors.ts';
import { api } from '../api/instance.ts';
import type { CheckoutRequest } from '../api/types.ts';
import { navigateTo } from '../app/nav.ts';
import { toast } from '../state/feedback.ts';

const back = (path: string) => `${location.origin}${path}`;

async function redirect(fn: () => Promise<{ url: string }>, fallback: string): Promise<boolean> {
  if (!useAccount.getState().session) {
    await navigateTo({ to: '/signin', search: { next: location.pathname } });
    return false;
  }
  if (isDemo(useAccount.getState().me)) {
    toast(DEMO_BILLING_NOTE, 'error');
    return false;
  }
  try {
    const { url } = await fn();
    location.assign(url);
    return true;
  } catch (e) {
    toast(apiMessage(e, fallback), 'error');
    return false;
  }
}

/** Stripe Checkout for a personal plan (Starter / Pro / Max). */
export function checkoutPlan(plan: string, interval: 'month' | 'year'): Promise<boolean> {
  return redirect(
    () =>
      api.checkout({
        plan: plan as CheckoutRequest['plan'],
        interval,
        successUrl: back('/account?checkout=success'),
        cancelUrl: back('/plans?checkout=canceled'),
      }),
    'Checkout failed.',
  );
}

/** Stripe Checkout for Team seats (owner/admin of `orgId`). */
export function checkoutTeam(orgId: string, seats: number, interval: 'month' | 'year'): Promise<boolean> {
  return redirect(
    () =>
      api.checkout({
        plan: 'team',
        interval,
        orgId,
        seats: Math.max(2, Math.min(500, seats)),
        successUrl: back(`/team/${orgId}?checkout=success`),
        cancelUrl: back(`/team/${orgId}?checkout=canceled`),
      }),
    'Checkout failed.',
  );
}

/** One-time credit pack (Free users too). `orgId` buys into the company wallet. */
export function buyCreditPack(pack: string, orgId?: string): Promise<boolean> {
  return redirect(
    () =>
      api.checkoutPack({
        packId: pack,
        orgId,
        successUrl: back('/account?checkout=success'),
        cancelUrl: back(location.pathname),
      }),
    'Checkout failed.',
  );
}

/** Stripe Customer Portal (personal web plan, or the org's Team plan). */
export function openBillingPortal(orgId?: string): Promise<boolean> {
  return redirect(() => api.billingPortal({ orgId, returnUrl: location.href }), 'Could not open billing.');
}
